<template>
  <div class="mapping-preview-modal">
    <div v-if="loading" class="mapping-preview-modal__loading">
      <LoadingSpinner />
      <p>Preview wordt gegenereerd...</p>
    </div>

    <div v-else-if="error" class="mapping-preview-modal__error">
      <Message variant="danger" :text="error" />
    </div>

    <div v-else>
      <div v-if="warnings.length > 0" class="mb-3">
        <Message
          v-for="(w, i) in warnings"
          :key="i"
          variant="warning"
          :text="w"
        />
      </div>

      <div class="mapping-preview-modal__output">
        <Label text="Bestelling output" />
        <pre class="mapping-preview-modal__code">{{ previewText }}</pre>
      </div>

      <div v-if="resolvedValues.length > 0" class="mt-3">
        <Label text="Opgeloste waarden" />
        <div class="mapping-preview-modal__values">
          <div
            v-for="(rv, i) in resolvedValues"
            :key="i"
            class="mapping-preview-modal__value-row"
          >
            <code>{{ rv.code }}</code>
            <span>{{ rv.value }}</span>
            <span class="text-muted">{{ rv.source }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue';
import { productMappingService } from '@/services/productMappingService';
import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
import Message from '@/components/partials/Message.vue';
import Label from '@/components/partials/Label.vue';

export default defineComponent({
  name: 'MappingPreviewModal',
  components: { LoadingSpinner, Message, Label },

  props: {
    productId: { type: String, required: true },
    supplierId: { type: String, required: true },
  },

  setup(props) {
    const loading = ref(true);
    const error = ref(null);
    const previewText = ref('');
    const warnings = ref([]);
    const resolvedValues = ref([]);

    async function loadPreview() {
      loading.value = true;
      error.value = null;
      try {
        const result = await productMappingService.previewMapping(props.productId, {
          supplier_id: props.supplierId,
        });
        previewText.value = result.preview || '';
        warnings.value = result.warnings || [];
        resolvedValues.value = result.resolved_values || [];
      } catch (e) {
        error.value = e.response?.data?.message || 'Fout bij het genereren van de preview.';
      } finally {
        loading.value = false;
      }
    }

    onMounted(loadPreview);

    return { loading, error, previewText, warnings, resolvedValues };
  },
});
</script>

<style scoped lang="scss">
.mapping-preview-modal {
  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem;
    gap: 1rem;
    color: var(--text-secondary);
  }

  &__error {
    padding: 1rem 0;
  }

  &__output {
    margin-top: 0.5rem;
  }

  &__code {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    padding: 1rem;
    font-family: 'Fira Code', 'Courier New', monospace;
    font-size: 0.8125rem;
    white-space: pre-wrap;
    word-break: break-all;
    max-height: 400px;
    overflow-y: auto;
  }

  &__values {
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    overflow: hidden;
  }

  &__value-row {
    display: flex;
    gap: 1rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    border-bottom: 1px solid var(--border-color-light);

    &:last-child { border-bottom: none; }

    code {
      font-family: 'Fira Code', monospace;
      color: var(--color-primary);
      min-width: 80px;
    }
  }
}

.mt-3 { margin-top: 1rem; }
.mb-3 { margin-bottom: 1rem; }
.text-muted { color: var(--text-secondary); }
</style>
